import { cn } from "@/lib/utils";
import * as shared from "@/styles/shared";
import type { ButtonSize, ButtonVariant, IconComponent, RippleState } from "@/types/ui";
import { cva } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { forwardRef, useRef, useState } from "react";
import * as styles from "./Button.styles";

const buttonVariants = cva(
  "inline-flex items-center justify-center relative overflow-hidden whitespace-nowrap rounded-md text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground shadow hover:shadow-md hover:bg-primary/90",
        destructive: "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline: "border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        ghostHover: "hover:bg-accent/80 hover:text-accent-foreground border border-transparent hover:border-border/50",
        link: "text-primary underline-offset-4 hover:underline",
        gradient: "gradient-brand text-primary-foreground shadow-md hover:shadow-lg",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children?: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  iconOnly?: boolean;
  leftIcon?: IconComponent;
  rightIcon?: IconComponent;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, children, loading = false, iconOnly = false, leftIcon: LeftIcon, rightIcon: RightIcon, disabled, ...props }, ref) => {
    const [ripple, setRipple] = useState<RippleState | null>(null);
    const btnRef = useRef<HTMLButtonElement>(null);

    const handleRipple = (e: React.MouseEvent<HTMLButtonElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const id = Date.now();
      setRipple({ x, y, id });
      setTimeout(() => setRipple(null), 600);
    };

    const finalSize = iconOnly ? "icon" : size;
    const btnClass = cn(buttonVariants({ variant, size: finalSize, className }), loading && "pointer-events-none");

    return (
      <button
        ref={ref || btnRef}
        className={btnClass}
        disabled={disabled || loading}
        onClick={(e) => {
          handleRipple(e);
          props.onClick?.(e);
        }}
        {...props}
      >
        {ripple && (
          <span
            className={styles.ripple}
            style={{
              left: ripple.x - 8,
              top: ripple.y - 8,
              width: 16,
              height: 16,
              animation: "ping 0.6s 1",
            }}
          />
        )}
        {loading && (
          <span className={styles.loadingWrapper}>
            <Loader2 className={`${shared.iconSm} animate-spin`} />
          </span>
        )}
        {!loading && (
          <>
            {LeftIcon && <LeftIcon className={cn(shared.iconSm, iconOnly ? "" : styles.iconLeft)} />}
            {children}
            {RightIcon && <RightIcon className={cn(shared.iconSm, iconOnly ? "" : styles.iconRight)} />}
          </>
        )}
      </button>
    );
  },
);
Button.displayName = "Button";

export { Button };
